import type {
  AgenticLogicRuleDto,
  AgenticLogicSurface,
} from "@intelligems/ig-types";

export interface AgenticLogicRuleOptions {
  /** Rules the merchant may pick, name-sorted. */
  selectable: { id: string; name: string }[];
  /**
   * True when the block's saved rule is not in `selectable` — it was paused
   * or deleted after being attached. The picker keeps showing it so
   * the merchant sees what is attached rather than a blank control that would
   * detach the block on the next save.
   */
  selectedIsUnavailable: boolean;
}

/**
 * What the rule picker may offer for a block.
 *
 * Two filters, and both mirror a server refusal rather than adding a rule of
 * their own:
 *
 * - Only `active` rules are offered: a draft or paused rule resolves to nothing
 *   at serve time (`AgenticLogicAssignmentResolverService` requires
 *   `state === "active"`), so offering one would be offering a choice that
 *   silently renders an empty block.
 * - Only rules that serve `surface` are offered: `assertRuleServesSurface`
 *   (`agentic-logic-placement-assignments.service.ts`) refuses a rule whose
 *   `targetSurfaces` omits the placement's surface with a 409, so the picker
 *   must not offer one. A nullish declaration is NOT "serves nowhere" — it is a
 *   builder rule whose template registry governs, and it stays legal
 *   everywhere. `AttachRuleDialog` mirrors the same predicate for the
 *   assignments tab.
 */
export const buildAgenticLogicRuleOptions = (
  rules: readonly AgenticLogicRuleDto[],
  surface: AgenticLogicSurface,
  selectedRuleId: string | null | undefined
): AgenticLogicRuleOptions => {
  const selectable = rules
    .filter(
      (rule) =>
        rule.state === "active" &&
        (rule.targetSurfaces === null ||
          rule.targetSurfaces === undefined ||
          rule.targetSurfaces.includes(surface))
    )
    .map((rule) => ({ id: rule.id, name: rule.name }))
    .sort((a, b) => a.name.localeCompare(b.name));

  return {
    selectable,
    selectedIsUnavailable:
      !!selectedRuleId && !selectable.some((r) => r.id === selectedRuleId),
  };
};
